import type {ReactNode} from 'react'
import {Stack} from '@primer/react'
import {clsx} from 'clsx'
import styles from './UserMessage.module.css'

export interface UserMessageProps {
  /**
   * The message content: a plain text string, or a pre-rendered node (e.g.
   * when the caller prettifies pasted code before display).
   */
  text: ReactNode
  /**
   * Optional avatar rendered alongside the message bubble (e.g. a `GitHubAvatar`).
   */
  avatar?: ReactNode
  /**
   * Optional media rendered above the message text, inside the bubble (e.g.
   * inline image previews). Renders nothing when omitted.
   */
  media?: ReactNode
  /**
   * Optional references/attachments rendered at the top of the bubble, above
   * any media and text (e.g. a `ReferenceChipList` of `ReferenceChip`s).
   * Renders nothing when omitted.
   */
  references?: ReactNode
  /**
   * Optional control rendered immediately to the left of the bubble, within the
   * right-aligned group (e.g. a cancel control for a queued message). Renders
   * nothing when omitted.
   */
  leading?: ReactNode
  /** Optional content rendered directly beneath the bubble. */
  footer?: ReactNode
  /**
   * Optional class applied to the message bubble so callers can reuse the shared
   * layout while adjusting the bubble treatment for specific states.
   */
  bubbleClassName?: string
}

/**
 * Pure UI for a single user message: a muted bubble containing optional
 * attachment references at the top, then optional media (e.g. image previews),
 * then the text (or pre-rendered content), with an optional avatar alongside.
 * No context, no data fetching, no attachment parsing; callers pass
 * display-ready references, media, text, and avatar.
 */
export function UserMessage({text, avatar, media, references, leading, footer, bubbleClassName}: UserMessageProps) {
  return (
    <Stack align="start" direction="horizontal" gap="condensed" className={styles.row}>
      <div className={styles.content}>
        <div className={styles.message}>
          <div className={styles.messageRow}>
            {leading}
            <div className={clsx(styles.bubble, bubbleClassName)}>
              {references ? <div className={styles.references}>{references}</div> : null}
              {media}
              {text}
            </div>
          </div>
          {footer}
        </div>
      </div>
      {avatar ? <div className={styles.avatarContainer}>{avatar}</div> : null}
    </Stack>
  )
}
